import { FC, ReactElement } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  capitalize,
  errors,
  innerItemEditStyles,
  useFormats,
} from "@ryde/shared";

import {
  ColorIconButton,
  MultiSelectBoundInputByControl,
} from "../../../controls";
import { TimeTemplate } from "../time.template";
import {
  catchARideDayConditions,
  catchARideEntityConditions,
  ICatchARideAvailabilityItemForm,
  ICatchARideAvailabilityListCondition,
} from "./catch-a-ride-availability.models";
import { useCatchARideAvailabilityItemEdit } from "./use-catch-a-ride-availability-item-edit";

export const CatchARideAvailabilityItemEdit: FC<
  ICatchARideAvailabilityItemForm
> = ({ value, onCancelOuter, setValueOuter, fieldName }) => {
  const { t } = useTranslation();
  const { timeFormat } = useFormats();
  const { methods, editorRef, hasAnyCondition, canSave, onSaveClick } =
    useCatchARideAvailabilityItemEdit({
      fieldName,
      value,
      setValueOuter,
      onCancelOuter,
    });

  const renderCondition = (
    condition: ICatchARideAvailabilityListCondition,
  ): ReactElement => (
    <MultiSelectBoundInputByControl
      key={condition.name}
      name={condition.name}
      control={methods.control}
      formLabel={
        condition.isLabelCapitalized
          ? capitalize(t(condition.labelKey))
          : t(condition.labelKey)
      }
      formAttributes={{ dropItemsSource: condition.dropItemsSource }}
    />
  );

  return (
    <FormProvider {...methods}>
      <div
        className="availability-item no-blur-item"
        style={innerItemEditStyles}
      >
        <div className="availability-item__editors" ref={editorRef}>
          {catchARideDayConditions.map(renderCondition)}
          <TimeTemplate
            key="from-time"
            timeFormat={timeFormat}
            name="from"
            labelTranslated={t("general.FROM")}
          />
          <TimeTemplate
            key="to-time"
            timeFormat={timeFormat}
            name="to"
            labelTranslated={t("general.TO")}
          />
          {catchARideEntityConditions.map(renderCondition)}
          {!hasAnyCondition && (
            <div className="availability-item__hint" role="status">
              {t(errors.validation.atLeastOneConditionRequired)}
            </div>
          )}
        </div>
        <div className="availability-item__buttons">
          <ColorIconButton
            disabled={!canSave}
            iconType="CHECK"
            edge="end"
            aria-label={t("general.SAVE")}
            onClick={onSaveClick}
          />
          <ColorIconButton
            iconType="CANCEL"
            edge="end"
            aria-label={t("general.CANCEL")}
            onClick={() => onCancelOuter?.()}
          />
        </div>
      </div>
    </FormProvider>
  );
};
